import { MRT_ColumnDef, createMRTColumnHelper } from "material-react-table";
import { Box, useTheme } from "@mui/material";
import { FoodsIcon, FoodsTooltip } from "@foodpilot/foods";
import { useTranslation } from "react-i18next";
import { GridText, GridTextList } from "src/components/Grid/GridText";
import { IngredientList } from "src/api/client-api/ingredients/useGetAllIngredients";
import {
    extendColumnsByScores,
    extendColumnsByJsonAttributes,
} from "src/components/Grid/hooks/columnsExtension/useGridColumns";
import { GridLink } from "src/components/Grid/GridLink";
import { useFoodpilotContext } from "src/context/FoodpilotContext";
import { URLHandler } from "src/services/URLHandler";
import { useGetSomeIngredientReferencesUnified } from "src/api/client-api/ingredientReferenceUnified/useGetSomeIngredientReferencesUnified";
import { IngredientReferenceUnified, buildCompositeId } from "src/api/client-api/ingredientReferenceUnified/types";
import { useTagContext } from "src/api/client-api/tag";
import { generateTagColumns } from "src/utils/tagColumns";
import { StringFilter } from "src/components/Grid/filters/StringFilter";
import { SelectManyFromFilter } from "src/components/Grid/filters/SelectManyFromFilter/SelectManyFromFilter";
import { IngredientReferenceFilter } from "src/components/Grid/filters/IngredientReferenceFilter/IngredientReferenceFilter";
import { IngredientReferenceFilterChipValue } from "src/components/Grid/filters/IngredientReferenceFilter/IngredientReferenceFilterChipValue";
import { useGetAllIngredientLines } from "src/api/client-api/ingredientLines/useGetAllIngredientLine";
import { useCompanyMetadata } from "src/context/Company/Metadata/useCompanyMetadata";
import { ScoreFilter } from "src/components/Grid/filters/ScoreFilter";
import { FoodsLoadingIndicator } from "src/components/EntityForm/FoodsFields";
import { useIsProductUxV2 } from "src/hooks/useIsProductUxV2";
import { JSON_ATTRIBUTE_ENTITY_CLASS } from "src/constants";

export type IngredientListColumns = MRT_ColumnDef<IngredientList, any>[];

export type MrtColumnsIngredient = {
    mrt_ingredientColumns: IngredientListColumns;
};

export const useIngredientColumns = (data?: IngredientList[]): MrtColumnsIngredient => {
    const { t } = useTranslation();
    const theme = useTheme();
    const isProductUxV2 = useIsProductUxV2();
    const appearance = isProductUxV2 ? "product-v2" : "legacy";

    const { company } = useCompanyMetadata();
    const { allIngredientLines, searchApi: ingredientLineSearchApi } = useGetAllIngredientLines({
        itemsPerPage: 1000,
        initSearch: "",
    });
    const { isUserOfTypeSupplier, getScoresByType, entitiesJsonAttributes } = useFoodpilotContext();

    // Build composite ids from (referenceSource, ingredientReferenceId) so the unified
    // endpoint returns the right name regardless of the source. Default to "sure" for
    // historical rows that predate the source column.
    const ingredientCompositeIds =
        data?.flatMap((ing) => {
            if (ing.ingredientReferenceId === undefined) return [];
            return [buildCompositeId(ing.referenceSource ?? "sure", ing.ingredientReferenceId)];
        }) ?? [];

    const { ingredientReferences } = useGetSomeIngredientReferencesUnified(ingredientCompositeIds);
    const ingredientsScores = getScoresByType("ingredient");
    const ingredientJsonAttributes = entitiesJsonAttributes?.[JSON_ATTRIBUTE_ENTITY_CLASS.ingredient] || [];
    const { tagTypes, tagValues } = useTagContext("ingredient");

    const compositeIdToRef = ingredientReferences?.reduce(
        (acc, ref) => {
            acc[ref.compositeId] = ref;
            return acc;
        },
        {} as Record<string, IngredientReferenceUnified>,
    );

    const columnHelper = createMRTColumnHelper<IngredientList>();

    const noValue = "-";
    const mrt_ingredientColumns: MRT_ColumnDef<IngredientList, any>[] = [];
    mrt_ingredientColumns.push(
        columnHelper.accessor((row) => row.name, {
            grow: false,
            id: "name",
            header: t("nom"),
            Cell: (value) => {
                const ingredientId = value.row.original.id;
                const url = URLHandler.ingredient.scores(company.id, ingredientId);

                if (value.row.original.canView) {
                    return <GridLink appearance={appearance} to={url} label={value.renderedCellValue} />;
                }
                return <GridText appearance={appearance} value={value.renderedCellValue} />;
            },
            Filter: (mrtProps) => {
                return <StringFilter mrtProps={mrtProps} />;
            },
            enableSorting: true,
            enableColumnFilter: true,
        }),
    );

    if (!isUserOfTypeSupplier()) {
        mrt_ingredientColumns.push(
            columnHelper.accessor((row) => row.clientId || noValue, {
                id: "clientId",
                header: t("unique_identifier"),
                Cell: (value) => <GridText appearance={appearance} value={value.cell.getValue()} />,

                Filter: (mrtProps) => {
                    return <StringFilter mrtProps={mrtProps} />;
                },
                enableSorting: true, // Server-side sorting handled by API Platform OrderFilter
                enableColumnFilter: true,
            }),
        );
    }

    mrt_ingredientColumns.push(
        columnHelper.accessor((row) => row.ingredientReferenceId, {
            // Column id matches the URL param name `ingredientReferenceCompositeId[]` consumed by
            // the backend `IngredientReferenceCompositeIdFilter`. Numeric `ingredientReferenceId`
            // alone is ambiguous between sources (sure vs foodpilot share the same id space).
            id: "ingredientReferenceCompositeId",
            header: t("reference"),
            Cell: (value) => {
                const refId = value.cell.getValue();
                const refSource = value.row.original.referenceSource ?? "sure";

                if (!refId) return <GridText appearance={appearance} value={noValue} />;
                if (!compositeIdToRef) {
                    return (
                        <FoodsLoadingIndicator label={t("common.loading", "Loading...")} size={theme.spacing(2.25)} />
                    );
                }

                const compositeId = buildCompositeId(refSource, refId);
                const ingToShow = compositeIdToRef[compositeId];
                const name = ingToShow?.name ?? noValue;

                if (refSource === "foodpilot") {
                    return (
                        <Box sx={{ display: "inline-flex", alignItems: "center", gap: theme.spacing(0.5) }}>
                            <FoodsTooltip
                                title={t(
                                    "ingredientReferences.referenceSourceTooltipPlatform",
                                    "Platform-defined reference (FoodPilot)",
                                )}
                            >
                                <Box component="span" sx={{ display: "inline-flex", alignItems: "center" }}>
                                    <FoodsIcon icon="organization" size={2} color={theme.palette.primary.main} />
                                </Box>
                            </FoodsTooltip>
                            <GridText appearance={appearance} value={name} />
                        </Box>
                    );
                }

                return <GridText appearance={appearance} value={name} />;
            },

            Filter: (mrtProps) => {
                return <IngredientReferenceFilter mrtProps={mrtProps} />;
            },
            meta: {
                renderFilterChipValue: (value: unknown) => {
                    const ids =
                        Array.isArray(value) ? value.map(String)
                        : typeof value === "string" && value !== "" ? [value]
                        : [];
                    if (ids.length === 0) return null;
                    return <IngredientReferenceFilterChipValue compositeIds={ids} />;
                },
            },
            enableSorting: false, // Reference sorting not supported (displays name but ID is stored)
            enableColumnFilter: true,
        }),
    );
    mrt_ingredientColumns.push(
        columnHelper.accessor((row) => row.ingredientLine?.name ?? noValue, {
            id: "ingredientLine.name",
            header: t("filiere"),
            Cell: (value) => {
                const v = value.cell.getValue();
                if (Array.isArray(v)) {
                    return <GridTextList appearance={appearance} value={v} separator=", " />;
                }

                return <GridText appearance={appearance} value={value.renderedCellValue} />;
            },
            Filter: (mrtProps) => {
                return (
                    <SelectManyFromFilter
                        appearance={appearance}
                        selectOptions={{
                            kind: "checkbox",
                            values: allIngredientLines.map((l) => ({ id: l.id, name: l.name })),
                            getId: (item) => item.id,
                            getName: (item) => item.name,
                        }}
                        mrtProps={mrtProps}
                        searchApi={ingredientLineSearchApi}
                    />
                );
            },
            enableSorting: true,
            enableColumnFilter: true,
        }),
    );

    extendColumnsByScores(mrt_ingredientColumns, data ?? [], ingredientsScores, {
        getReferenceTitle: (scoreTitle) => {
            return t("grid.column.header.reference_score", "{{scoreTitle}} reference", { scoreTitle });
        },
        getComparisonTitle: (scoreTitle) => {
            return t("grid.column.header.comparison_score", "{{scoreTitle}} Δ%", { scoreTitle });
        },
        enableFiltering: true,
        enableSorting: true, // Applies to reference scores
        enableOlapSorting: true, // Server-side sorting via IngredientScoreOrderFilter
        FilterComponent: ScoreFilter,
        appearance,
    });

    // Add dynamic JSON attribute columns
    extendColumnsByJsonAttributes(mrt_ingredientColumns, ingredientJsonAttributes, data, { appearance });

    if (!isUserOfTypeSupplier()) {
        mrt_ingredientColumns.push(
            columnHelper.accessor("ingredientSuppliersCount", {
                header: t("suppliers"),
                enableSorting: false, // Suppliers sorting not supported server-side (array relationship)
                Cell: (value) => {
                    const ownedById = value.row.original.ownedBy?.id;
                    const ingSuppliersCount = value.cell.getValue();

                    return <GridText appearance={appearance} value={ingSuppliersCount} />;
                },
            }),
        );
    }

    // Add dynamic tag columns (hidden by default, can be enabled via column management)
    if (tagTypes && tagTypes.length > 0) {
        const tagColumns = generateTagColumns<IngredientList>({
            appearance: isProductUxV2 ? "product-v2" : "legacy",
            tagTypes,
            tagValues,
            onTagChange: (entityId, tagTypeSlug, values) => {
                // Tag updates are handled through the main ingredient form
                // This is read-only display for the ingredient list
            },
            readonly: true, // Set to true for read-only columns displaying pills
        });

        mrt_ingredientColumns.push(...tagColumns);
    }

    return { mrt_ingredientColumns };
};
